import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Added useNavigate
import axios from "axios";
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Container,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { API_ENDPOINT, ADMIN } from "../../utils/constants"; // Added ADMIN
import { getHeader, toastError } from "../../utils/util"; // Added toastError
import BackBtn from "../../widgets/BackBtn";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify"; // Added toast import
import SimpleYesNoDialog from "../widgets/SimpleYesNoDialog";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
const StaffDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate(); // Added navigate hook
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false); // Added delete dialog state
  const [deleting, setDeleting] = useState(false); // Added deleting state

  useEffect(() => {
    const fetchStaffDetail = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_ENDPOINT}admin/get-staff-detail?id=${id}`,
          getHeader()
        );
        setStaff(response.data);
        setError(null);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch staff details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDetail();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: "1200px", mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!staff) {
    return (
      <Box sx={{ p: 2, maxWidth: "1200px", mx: "auto" }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          No staff details found
        </Typography>
      </Box>
    );
  }

  return (
    <Container>
      <SimpleYesNoDialog
        title={"Please confirm!"}
        message={"Are you sure you want to delete this staff member?"}
        onYes={async () => {
          try {
            setDeleting(true);
            const response = await axios.delete(
              `${API_ENDPOINT}admin/delete-user?id=${staff._id}`,
              getHeader()
            );
            toast.success(response.data.message);
            setTimeout(() => {
              navigate(-1);
            }, 300);
          } catch (error) {
            console.log(error);
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
        show={deleteOpen}
        setShow={setDeleteOpen}
      />
      <BackBtn />
      <Card
        sx={{
          maxWidth: "800px",
          mx: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-start" },
              gap: 3,
              mb: 3,
            }}
          >
            <Avatar
              src={staff.profileImage}
              alt={staff.name}
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                border: "3px solid #eee",
              }}
            />
            <Box sx={{ textAlign: { xs: "center", sm: "left" }, flexGrow: 1 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  mb: 1,
                }}
              >
                {staff.name}
              </Typography>
              <Typography
                variant="subtitle1"
                color="primary"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: "medium",
                }}
              >
                {staff.designation}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      wordBreak: "break-word",
                    }}
                  >
                    {staff.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                  >
                    {staff.company}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocalPhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      wordBreak: "break-word",
                    }}
                  >
                    {staff.phone || ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <QrCodeIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    QR Code
                  </Typography>
                  <Typography
                    variant="body1"
                    component="a"
                    href={staff.qrLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View QR Code
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WorkIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Barcode ID
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                  >
                    {staff.barcodeId}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WorkIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created On
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                  >
                    {new Date(staff.createdOn).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box my={2} display={"flex"} justifyContent={"center"}>
            <LoadingButton
              loading={deleting}
              onClick={() => {
                setDeleteOpen(true);
              }}
              variant="outlined"
              size="small"
              color="error"
            >
              Delete Staff
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default StaffDetail;
